import React from 'react';
import { SidebarProvider, Button, useSidebar } from '@nimbitlabs/ui';
import { Minimize2, Maximize2 } from 'lucide-react';
import { AppSidebar, type NavGroup } from '../AppSidebar';
import { TopNavigation } from '../TopNavigation';
import { NotificationProvider } from '@nimbitlabs/features';
import { useAuth } from '@nimbitlabs/auth';
import { toast } from 'sonner';
import { useSidebarStore } from '../stores/sidebarStore';

interface AppLayoutProps {
  children: React.ReactNode;
  appName: string;
  logo?: React.ReactNode;
  appNavItems: NavGroup[];
  showManagementItems?: boolean;
  apiBaseUrl?: string;
  contentRibbon?: React.ReactNode;
}

export function AppLayout({
  children,
  appName,
  logo,
  appNavItems,
  showManagementItems = true,
  apiBaseUrl,
  contentRibbon,
}: AppLayoutProps) {
  const { user } = useAuth();
  
  // Use Zustand store for sidebar state
  const { sidebarGroupsCollapsed, toggleSidebarGroups } = useSidebarStore();

  const handleFeedbackSubmit = async (feedback: { category: string; message: string; email?: string }) => {
    try {
      const response = await fetch(`${apiBaseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          ...feedback,
          userId: user?.id,
          userEmail: user?.email || feedback.email,
          appName,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      toast.success('Feedback submitted successfully!');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
      throw error;
    }
  };

  // Footer component that can access sidebar state
  const SidebarFooterContent = () => {
    const { state } = useSidebar();
    const { sidebarGroupsCollapsed, toggleSidebarGroups } = useSidebarStore();
    
    return (
      <div className="flex items-center gap-2 p-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2 h-10 bg-sidebar-accent hover:bg-sidebar-accent/80"
          title={sidebarGroupsCollapsed ? 'Expand Menu Groups' : 'Collapse Menu Groups'}
          onClick={() => {
            console.log('[AppLayout] Button clicked, calling toggleSidebarGroups');
            toggleSidebarGroups();
          }}
        >
          {sidebarGroupsCollapsed ? (
            <Maximize2 className="h-4 w-4" />
          ) : (
            <Minimize2 className="h-4 w-4" />
          )}
          {state !== "collapsed" && (
            <span className="text-sm">
              {sidebarGroupsCollapsed ? 'Expand Groups' : 'Collapse Groups'}
            </span>
          )}
        </Button>
      </div>
    );
  };

  return (
    <NotificationProvider apiBaseUrl={apiBaseUrl}>
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <TopNavigation 
            appName={appName}
            onFeedbackSubmit={handleFeedbackSubmit} 
          />
          <AppSidebar
            logo={logo}
            appNavItems={appNavItems}
            showManagementItems={showManagementItems}
            footer={<SidebarFooterContent />}
          />
          <main className="flex-1 flex flex-col pt-16 w-full overflow-hidden items-center">
            <div className="w-full max-w-[1400px] flex-1 flex flex-col overflow-hidden px-6">
              {contentRibbon && (
                <div className="w-full pt-6 pb-0">
                  {contentRibbon}
                </div>
              )}
              <div className="flex-1 overflow-auto py-6">
                {children}
              </div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    </NotificationProvider>
  );
}
